import React, { useState, useEffect } from "react"
import { TrashIcon, PlusCircleIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Input from "aldoo-ra/Input"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"
import UpdatesController from "./updates-controller"
import UpdateDialog from "./add-update-dialog"
import DependenciesDialog from "./dependencies-dialog"
import CreateUpdateDialog from "./create-update-dialog"
import EnvironmentContentInstallDialog from "../Environments/EnvironmentContentInstallDialog"
import { api, generateCRUDInterface } from "aldoo-ra/API"

// Create a single controller instance
const controller = UpdatesController()

const UpdateConfigViewer = () => {
  const [refreshKey, setRefreshKey] = useState(0)
  const [showDialog, setShowDialog] = useState(false)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingItem, setEditingItem] = useState(null)
  const [showDependencies, setShowDependencies] = useState(false)
  const [showInstallDialog, setShowInstallDialog] = useState(false)

  // Subscribe to controller state changes
  useEffect(() => {
    const unsubscribe = controller.subscribe(() => {
      setRefreshKey((prev) => prev + 1)
    })
    return () => unsubscribe()
  }, [])

  const getUpdatesData = async () => {
    const items = controller.getItems()
    return {
      data: items,
      totalItems: items.length,
    }
  }

  const handleRemove = (item) => {
    showPromptDialog({
      title: "Confirm Delete",
      message: "Are you sure you want to delete this configuration?",
      confirmText: "Delete",
      onConfirm: () => {
        controller.remove(item.id)
      },
    })
  }

  const handleAddNew = () => {
    setEditingItem(controller.create())
    setShowDialog(true)
  }

  const handleExport = () => {
    if (controller.getItems().length === 0) {
      showPromptDialog({
        title: "No Configurations",
        message: "Please add at least one configuration before exporting.",
        confirmText: "OK",
      })
      return
    }
    setShowCreateDialog(true)
  }

  const handleInstallOnEnvironment = () => {
    if (controller.getItems().length === 0) {
      showPromptDialog({
        title: "No Configurations",
        message: "Please add at least one configuration before installing.",
        confirmText: "OK",
      })
      return
    }
    setShowInstallDialog(true)
  }

  const getInstallTarget = () =>
    controller.getItems().map((item) => ({
      type: item.type.value,
      match: item.query.match || undefined,
    }))

  const tableItemRender = [
    {
      id: "type",
      title: "Type",
      headerClassName: "w-[30%]",
      render: (value, item) => (
        <Typography variant="admin_body">
          {item.type?.label || "Not Set"}
        </Typography>
      ),
    },
    {
      id: "details",
      title: "Details",
      headerClassName: "w-[35%]",
      render: (value, item) => (
        <Typography variant="admin_body">
          {item.details || "No details"}
        </Typography>
      ),
    },
    {
      id: "items",
      title: "Items",
      headerClassName: "w-[10%]",
      render: (value, item) => (
        <Typography variant="admin_body">
          {item.itemCount}
        </Typography>
      ),
    },
    {
      id: "actions",
      headerClassName: "w-[15%]",
      render: (value, item) => (
        <div className="flex gap-2">
          <Button 
            variant="admin_secondary" 
            className="ml-auto"
            onClick={() => handleViewDetails(item)}
          >
            Details
          </Button>
        </div>
      ),
    },
    {
      id: "remove",
      headerClassName: "w-[10%]",
      render: (value, item) => (
        <Button 
          variant="admin_icon" 
          className="text-admin_neutral_3 hover:text-admin_error mx-auto"
          onClick={() => handleRemove(item)}
        >
          <TrashIcon className="w-5 h-5 items-center" />
        </Button>
      ),
    },
  ]

  const handleViewDetails = (item) => {
    setEditingItem(item)
    setShowDialog(true)
  }

  const handleClearAll = () => {
    showPromptDialog({
      title: "Clear All Configurations",
      message:
        "Are you sure you want to clear all configurations? This action cannot be undone.",
      confirmText: "Clear All",
      onConfirm: controller.clearItems,
    })
  }

  const handleAddAldooCore = async () => {
    try {
      const crudAPI = api("crud")

      // Create CRUD interfaces for CMSType and CMSContent
      const typeCRUD = generateCRUDInterface(crudAPI, "CMSType")
      const contentCRUD = generateCRUDInterface(crudAPI, "CMSContent")

      // Query CMSType with aldooCore: true (with page param to get totalItems)
      const typesResult = await typeCRUD.Get({
        match: { aldooCore: true },
        page: 1
      })

      // Query CMSContent with aldooCore: true (with page param to get totalItems)
      const contentResult = await contentCRUD.Get({
        match: { aldooCore: true },
        page: 1
      })

      // Extract counts from paginated response
      const typesCount = typesResult?.totalItems || 0
      const contentCount = contentResult?.totalItems || 0

      if (typesCount === 0 && contentCount === 0) {
        showNotification({
          message: "No Aldoo Core content found",
          className: "bg-admin_warning text-white",
        })
        return
      }

      // Check if aldooCore items already exist in the package
      const existingItems = controller.getItems()
      const hasCMSType = existingItems.some(item =>
        item.type?.value === "CMSType" &&
        item.query?.match?.aldooCore === true
      )
      const hasCMSContent = existingItems.some(item =>
        item.type?.value === "CMSContent" &&
        item.query?.match?.aldooCore === true
      )

      let addedCount = 0

      // Add CMSType configuration if there are types and not already added
      if (typesCount > 0 && !hasCMSType) {
        const typeConfig = controller.create()
        typeConfig.type = { label: "Content Type", dataType: "model", value: "CMSType" }
        typeConfig.query = { match: { aldooCore: true } }
        typeConfig.details = `Aldoo Core Content Types (${typesCount} items)`
        typeConfig.itemCount = typesCount
        controller.add(typeConfig)
        addedCount += typesCount
      }

      // Add CMSContent configuration if there are content items and not already added
      if (contentCount > 0 && !hasCMSContent) {
        const contentConfig = controller.create()
        contentConfig.type = { label: "Content", dataType: "model", value: "CMSContent" }
        contentConfig.query = { match: { aldooCore: true } }
        contentConfig.details = `Aldoo Core Content (${contentCount} items)`
        contentConfig.itemCount = contentCount
        controller.add(contentConfig)
        addedCount += contentCount
      }

      if (addedCount > 0) {
        showNotification({
          message: `Added ${addedCount} Aldoo Core items to package`,
          className: "bg-admin_success text-white",
        })
      } else {
        showNotification({
          message: "Aldoo Core content already added to package",
          className: "bg-admin_warning text-white",
        })
      }
    } catch (error) {
      console.error("Error loading Aldoo Core content:", error)
      showNotification({
        message: "Failed to load Aldoo Core content",
        className: "bg-admin_error text-white",
      })
    }
  }

  // Table footer content - Clear All button
  const tableFooter = controller.getItems().length > 0 && (
    <div className="flex items-center justify-end">
      <Button 
        variant="admin_error" 
        onClick={handleClearAll}
      >
        Clear All
      </Button>
    </div>
  )

  return (
    <>
      <div className="p-4">
        {/* Tittle and Buttons */}
        <div className="w-full flex items-center justify-between mb-5">
          <Typography
            component="h1"
            variant="admin_h3"
            className="ml-4"
          >
            Export Package
          </Typography>

          <div className="flex items-center gap-3 ml-auto">
            {/* Aldoo Core Content button - always visible */}
            <Button
              variant="admin_secondary"
              onClick={handleAddAldooCore}
            >
              Aldoo Core Content
            </Button>

            {/* Only show these buttons when there are items */}
            {controller.getItems().length > 0 && (
              <>
                <Button
                  variant="admin_secondary"
                  onClick={() => setShowDependencies(true)}
                >
                  Dependencies
                </Button>

                <Button
                  variant="admin_secondary"
                  onClick={handleExport}
                >
                  Export
                </Button>

                <Button
                  variant="admin_primary"
                  onClick={handleInstallOnEnvironment}
                >
                  Install on environment
                </Button>
              </>
            )}
          </div>

          <Button
            variant="admin_primary"
            className="ml-3"
            onClick={handleAddNew}
          >
            <PlusCircleIcon className="w-5 h-5 mr-2" />
            Add
          </Button>
        </div>

        <DataTable
          variant="admin_browser"
          apiMethod={getUpdatesData}
          itemHeight="h-[60px]"
          searchField="details type.label"
          searchObject={{}}
          tableFooter={tableFooter}
          localUserPreferencesKey="admin.updates.configs"
        styleOverrides={{
            table: "w-full table-fixed",
            header: "w-full my-6",
            searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          }}
          renders={tableItemRender}
          refreshKey={refreshKey}
          noData={<Typography variant="admin_body">
                    No configurations found
                  </Typography>
                }
        />
      </div>

      <UpdateDialog
        isOpen={showDialog}
        onClose={() => {
          setShowDialog(false)
          setEditingItem(null)
        }}
        controller={controller}
        initialData={editingItem}
      />

      <DependenciesDialog
        isOpen={showDependencies}
        onClose={() => setShowDependencies(false)}
        controller={controller}
      />

      <CreateUpdateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        controller={controller}
      />

      {showInstallDialog && (
        <EnvironmentContentInstallDialog
          target={getInstallTarget()}
          contentName={`Export Selection (${controller.getItems().length} configuration${controller.getItems().length !== 1 ? "s" : ""})`}
          onClose={() => setShowInstallDialog(false)}
        />
      )}
    </>
  )
}

export default UpdateConfigViewer
